import { useQuery } from "@apollo/client";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { DateRangeValue } from "./DateRangeField";
import { ReportFilterState } from "./ReportFilters";
import { browserTimezone, formatCount, formatPeriod } from "./reportFormat";
import { ReportError, Section } from "./reportParts";
import { shade } from "./RetentionTab";
import { REPORT_RETENTION_CURVE, REPORT_RETENTION_DRIVERS, REPORT_SCORECARD } from "./scorecard.queries";
import {
  basisOf,
  ComparePreset,
  describeChange,
  DayWindow,
  formatDays,
  formatLift,
  formatMetric,
  formatRate,
  presetWindows,
  quarterLabel,
  SCORECARD_SECTIONS as SECTIONS,
  ScorecardUnit,
  SMALL_GROUP,
  toDay,
  Tone,
} from "./scorecardFormat";
import { TrendLine } from "./TrendLine";
import { regionLabel, ScorecardPdfButton } from "./pdf/exportButtons";

interface MetricValue extends DayWindow {
  value: number | null;
  numerator: number | null;
  denominator: number | null;
}

interface Metric {
  key: string;
  section: string;
  label: string;
  description: string;
  unit: ScorecardUnit;
  headline: boolean;
  higherIsBetter: boolean;
  target: number | null;
  settlesAfterDays: number;
  shiftedDays: number;
  current: MetricValue;
  previous: MetricValue;
  trend: MetricValue[];
}

/** Retention and activation are counted by the day members registered; the rest by the day it happened. */
const BY_REGISTRATION = new Set(["RETENTION", "ACTIVATION"]);


/** Success text and critical from the reference palette, per mode. Always shown with an arrow and words. */
const TONE = {
  light: { good: "#006300", bad: "#d03b3b" },
  dark: { good: "#0ca30c", bad: "#e66767" },
};

function useToneColor() {
  const theme = useTheme();
  const tones = TONE[theme.palette.mode === "dark" ? "dark" : "light"];
  return (tone: Tone) => (tone === "good" ? tones.good : tone === "bad" ? tones.bad : theme.palette.text.secondary);
}

const ChangeLabel: React.FC<{ metric: Metric; size?: "small" | "large" }> = ({ metric, size = "small" }) => {
  const color = useToneColor();
  const change = describeChange(metric.unit, metric.current.value, metric.previous.value, metric.higherIsBetter);
  if (change.direction === "none") {
    // A tile says nothing; a table cell shows a dash, named for screen readers.
    return size === "large" ? null : (
      <Typography component="span" variant="body2" color="text.secondary" aria-label="Nothing to compare">
        —
      </Typography>
    );
  }
  const Icon = change.direction === "up" ? ArrowUpwardIcon : change.direction === "down" ? ArrowDownwardIcon : null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.25,
        color: color(change.tone),
        fontWeight: 600,
        fontSize: size === "large" ? 15 : 14,
        whiteSpace: "nowrap",
      }}
    >
      {Icon && <Icon sx={{ fontSize: size === "large" ? 18 : 16 }} aria-hidden />}
      {change.label}
      {change.tone !== "flat" && (
        <Box component="span" sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>
          {change.tone === "good" ? " (better)" : " (worse)"}
        </Box>
      )}
    </Box>
  );
};

const trendPoints = (metric: Metric) =>
  metric.trend.map((week) => ({
    label: `${BY_REGISTRATION.has(metric.section) ? "registered " : ""}${formatDays(week)}`,
    value: week.value,
  }));

/** The members a rate is out of, and a warning when there are too few for it to mean much. */
const Basis: React.FC<{ metric: Metric; value: MetricValue; showDays: boolean }> = ({ metric, value, showDays }) => {
  const basis = basisOf(metric.unit, value.numerator, value.denominator);
  const small = metric.unit === "RATE" && value.denominator !== null && value.denominator > 0 && value.denominator < SMALL_GROUP;
  return (
    <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.4 }}>
      <Box component="div" sx={{ whiteSpace: "nowrap" }}>
        {basis}
      </Box>
      {small && <Box component="div">few members</Box>}
      {showDays && (
        <Box component="div" sx={{ whiteSpace: "nowrap" }}>
          {formatDays(value)}
        </Box>
      )}
    </Typography>
  );
};

const HeadlineTile: React.FC<{ metric: Metric }> = ({ metric }) => {
  const format = (value: number | null) => formatMetric(metric.unit, value);
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {metric.label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 1.25 }}>
        <Typography sx={{ fontSize: 32, fontWeight: 600, lineHeight: 1.2 }}>{format(metric.current.value)}</Typography>
        <ChangeLabel metric={metric} size="large" />
      </Box>
      <Typography variant="caption" color="text.secondary">
        {metric.previous.value !== null ? `was ${format(metric.previous.value)}` : "nothing to compare"}
        {metric.unit === "RATE" && " · "}
        {metric.unit === "RATE" && basisOf(metric.unit, metric.current.numerator, metric.current.denominator)}
      </Typography>
      {metric.target !== null && (
        <Typography variant="caption" color="text.secondary">
          Target {format(metric.target)}
        </Typography>
      )}
      {metric.shiftedDays > 0 && (
        <Typography variant="caption" color="text.secondary">
          Registered {formatDays(metric.current)}
        </Typography>
      )}
      <Box sx={{ mt: "auto", pt: 1 }}>
        <TrendLine points={trendPoints(metric)} format={format} title={`${metric.label}, last 12 weeks`} width={160} />
      </Box>
    </Paper>
  );
};

const MetricRow: React.FC<{ metric: Metric }> = ({ metric }) => {
  const [open, setOpen] = useState(false);
  const format = (value: number | null) => formatMetric(metric.unit, value);
  const shifted = metric.shiftedDays > 0;
  return (
    <>
      <TableRow sx={{ "& > td": { borderBottom: open ? "none" : undefined, verticalAlign: "top" } }}>
        <TableCell sx={{ minWidth: 220 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {metric.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ maxWidth: 420 }}>
            {metric.description}
            {metric.target !== null && ` Target: ${format(metric.target)}.`}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{format(metric.current.value)}</Typography>
          <Basis metric={metric} value={metric.current} showDays={shifted} />
        </TableCell>
        <TableCell align="right">
          <Typography sx={{ fontVariantNumeric: "tabular-nums" }}>{format(metric.previous.value)}</Typography>
          <Basis metric={metric} value={metric.previous} showDays={shifted} />
        </TableCell>
        <TableCell align="right">
          <ChangeLabel metric={metric} />
        </TableCell>
        <TableCell>
          <TrendLine points={trendPoints(metric)} format={format} title={`${metric.label}, last 12 weeks`} />
        </TableCell>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? "Hide the weeks" : "Show the weeks"} onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1.5, overflowX: "auto" }}>
              <Table size="small" aria-label={`${metric.label}, week by week`}>
                <TableHead>
                  <TableRow>
                    <TableCell>{BY_REGISTRATION.has(metric.section) ? "Members who registered" : "Week"}</TableCell>
                    <TableCell align="right">{metric.label}</TableCell>
                    {metric.unit === "RATE" && <TableCell align="right">Out of</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...metric.trend].reverse().map((week) => (
                    <TableRow key={week.from}>
                      <TableCell>{formatDays(week)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {format(week.value)}
                      </TableCell>
                      {metric.unit === "RATE" && (
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                          {basisOf(metric.unit, week.numerator, week.denominator)}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const MetricTable: React.FC<{ metrics: Metric[]; current: DayWindow; previous: DayWindow }> = ({ metrics, current, previous }) => (
  <Box sx={{ overflowX: "auto" }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Metric</TableCell>
          <TableCell align="right">
            This period
            <Typography variant="caption" component="div" color="text.secondary">
              {formatDays(current)}
            </Typography>
          </TableCell>
          <TableCell align="right">
            Compared with
            <Typography variant="caption" component="div" color="text.secondary">
              {formatDays(previous)}
            </Typography>
          </TableCell>
          <TableCell align="right">Change</TableCell>
          <TableCell>Last 12 weeks</TableCell>
          <TableCell padding="checkbox" />
        </TableRow>
      </TableHead>
      <TableBody>
        {metrics.map((metric) => (
          <MetricRow key={metric.key} metric={metric} />
        ))}
      </TableBody>
    </Table>
  </Box>
);

// ---- Months after joining --------------------------------------------------

interface CurveMonth {
  month: number;
  eligible: number;
  retained: number;
  rate: number | null;
}

interface CurveCohort {
  cohort: string;
  members: number;
  months: CurveMonth[];
}


const CURVE_INK = { light: "#2a78d6", dark: "#3987e5" };
const CURVE_HEIGHT = 200;
const CURVE_MARGIN = { top: 16, right: 16, bottom: 28, left: 44 };

/** The share still active in each month after joining, for everyone together. */
const CurveChart: React.FC<{ months: CurveMonth[] }> = ({ months }) => {
  const theme = useTheme();
  const ink = CURVE_INK[theme.palette.mode === "dark" ? "dark" : "light"];
  const holder = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    const element = holder.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(260, entry.contentRect.width)));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const known = months.filter((cell) => cell.rate !== null);
  const highest = Math.max(0.05, ...known.map((cell) => cell.rate as number));
  const top = Math.ceil(highest * 10) / 10;
  const innerWidth = width - CURVE_MARGIN.left - CURVE_MARGIN.right;
  const innerHeight = CURVE_HEIGHT - CURVE_MARGIN.top - CURVE_MARGIN.bottom;
  const band = innerWidth / months.length;
  const x = (index: number) => CURVE_MARGIN.left + band * index + band / 2;
  const y = (rate: number) => CURVE_MARGIN.top + innerHeight - (rate / top) * innerHeight;
  const path = known.map((cell, index) => `${index ? "L" : "M"}${x(cell.month - 1)},${y(cell.rate as number)}`).join("");
  const first = known[0];
  const last = known[known.length - 1];
  const shown = hovered === null ? null : months[hovered];

  return (
    <Box ref={holder} sx={{ position: "relative" }} onMouseLeave={() => setHovered(null)}>
      <svg
        width={width}
        height={CURVE_HEIGHT}
        role="img"
        aria-label="Share of members still active in each month after joining. The table below lists the same numbers."
        style={{ display: "block" }}
      >
        {[0, top / 2, top].map((tick) => (
          <g key={tick}>
            <line
              x1={CURVE_MARGIN.left}
              x2={width - CURVE_MARGIN.right}
              y1={y(tick)}
              y2={y(tick)}
              stroke={theme.palette.divider}
              strokeWidth={1}
            />
            <text x={CURVE_MARGIN.left - 6} y={y(tick)} dy="0.32em" textAnchor="end" fontSize={11} fill={theme.palette.text.secondary}>
              {Math.round(tick * 100)}%
            </text>
          </g>
        ))}
        {months.map((cell, index) => (
          <text key={cell.month} x={x(index)} y={CURVE_HEIGHT - 8} textAnchor="middle" fontSize={11} fill={theme.palette.text.secondary}>
            {cell.month}
          </text>
        ))}
        {hovered !== null && (
          <line x1={x(hovered)} x2={x(hovered)} y1={CURVE_MARGIN.top} y2={CURVE_MARGIN.top + innerHeight} stroke={theme.palette.divider} />
        )}
        <path d={path} fill="none" stroke={ink} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {known.map((cell) => (
          <circle
            key={cell.month}
            cx={x(cell.month - 1)}
            cy={y(cell.rate as number)}
            r={4}
            fill={ink}
            stroke={theme.palette.background.paper}
            strokeWidth={2}
          />
        ))}
        {/* The first and latest months carry their numbers; the rest are in the readout and the table. */}
        {[first, last]
          .filter((cell, index, list) => cell && list.indexOf(cell) === index)
          .map((cell) => (
            <text
              key={`label-${cell.month}`}
              x={x(cell.month - 1)}
              y={y(cell.rate as number) - 10}
              textAnchor="middle"
              fontSize={12}
              fontWeight={600}
              fill={theme.palette.text.primary}
            >
              {formatRate(cell.rate)}
            </text>
          ))}
        {months.map((cell, index) => (
          <rect
            key={`hit-${cell.month}`}
            x={CURVE_MARGIN.left + band * index}
            y={CURVE_MARGIN.top}
            width={band}
            height={innerHeight}
            fill="transparent"
            onMouseEnter={() => setHovered(index)}
          />
        ))}
      </svg>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center" }}>
        Months after registering
      </Typography>
      {shown && hovered !== null && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: Math.min(Math.max(x(hovered), 90), width - 90),
            transform: "translateX(-50%)",
            pointerEvents: "none",
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            boxShadow: 2,
            px: 1.5,
            py: 1,
            fontSize: 13,
            whiteSpace: "nowrap",
          }}
        >
          <Box sx={{ fontWeight: 600 }}>{shown.rate === null ? "Not over yet" : formatRate(shown.rate)}</Box>
          <Box sx={{ color: "text.secondary" }}>
            Month {shown.month} (days {shown.month * 30}–{shown.month * 30 + 29})
            {shown.eligible > 0 && ` · ${formatCount(shown.retained)} of ${formatCount(shown.eligible)}`}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const RetentionCurveSection: React.FC<{ scope: Record<string, string | null | undefined> }> = ({ scope }) => {
  const [cohortSize, setCohortSize] = useState<"QUARTER" | "MONTH">("QUARTER");
  const { data, previousData, loading, error } = useQuery(REPORT_RETENTION_CURVE, {
    variables: { input: { ...scope, cohortSize } },
    fetchPolicy: "cache-and-network",
  });
  const curve = (data ?? previousData)?.reportRetentionCurve;
  const rows: CurveCohort[] = curve ? [curve.overall, ...curve.cohorts] : [];
  const highest = Math.max(0, ...rows.flatMap((row) => row.months.map((cell) => cell.rate ?? 0)));
  const label = (row: CurveCohort) =>
    row.cohort === "all" ? "Everyone" : cohortSize === "QUARTER" ? quarterLabel(row.cohort) : formatPeriod(row.cohort, "MONTH");
  const monthOne = curve?.overall.months[0];

  return (
    <Section
      title="Do members stay? Months after joining"
      note={
        <>
          Of the members who registered since {curve ? formatDays({ from: curve.since, to: curve.since }) : "2025"}, the share who
          still used the app in each month after registering (month 1 is days 30 to 59). In an app people keep, the line levels off;
          if it keeps falling toward zero, it has not found its place in members' lives yet. The PMF audit's target for month 1 is
          35%{monthOne?.rate !== null && monthOne?.rate !== undefined ? `; it is ${formatRate(monthOne.rate)} now` : ""}.
        </>
      }
    >
      <ReportError error={error} />
      {loading && !curve && <LinearProgress />}
      {curve && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 120ms" }}>
          <CurveChart months={curve.overall.months} />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={cohortSize}
            onChange={(_, value) => value && setCohortSize(value)}
            sx={{ my: 1.5 }}
            aria-label="Group members by"
          >
            <ToggleButton value="QUARTER">By quarter</ToggleButton>
            <ToggleButton value="MONTH">By month</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ overflow: "auto", maxHeight: 460 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Registered</TableCell>
                  <TableCell align="right">Members</TableCell>
                  {curve.overall.months.map((cell: CurveMonth) => (
                    <TableCell key={cell.month} align="center">
                      Month {cell.month}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.cohort}>
                    <TableCell sx={{ fontWeight: row.cohort === "all" ? 600 : undefined, whiteSpace: "nowrap" }}>{label(row)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {formatCount(row.members)}
                    </TableCell>
                    {row.months.map((cell) => {
                      const style = cell.rate === null ? undefined : shade(cell.rate, highest);
                      return (
                        <TableCell
                          key={cell.month}
                          align="center"
                          title={
                            cell.eligible
                              ? `${formatCount(cell.retained)} of ${formatCount(cell.eligible)} members active in month ${cell.month}`
                              : "Not over yet for anyone in this group"
                          }
                          sx={{
                            bgcolor: style?.background,
                            color: style?.color,
                            borderBottom: "2px solid",
                            borderBottomColor: "background.paper",
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: row.cohort === "all" ? 600 : undefined,
                            minWidth: 64,
                          }}
                        >
                          {formatRate(cell.rate)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </Section>
  );
};

// ---- What makes members stay -----------------------------------------------

/** Two steps of the reference sequential blue, validated as an ordinal pair in each mode. */
const PAIR = {
  light: { did: "#1c5cab", didNot: "#86b6ef", muted: "#c3c2b7" },
  dark: { did: "#5598e7", didNot: "#184f95", muted: "#383835" },
};

const RateBar: React.FC<{ rate: number | null; scale: number; color: string; label: string }> = ({ rate, scale, color, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} aria-label={label}>
    <Box sx={{ flex: "1 1 120px", minWidth: 80, maxWidth: 220 }}>
      <Box
        sx={{
          height: 8,
          width: `${rate === null || scale === 0 ? 0 : Math.max(1, (rate / scale) * 100)}%`,
          bgcolor: color,
          borderRadius: "0 4px 4px 0",
        }}
      />
    </Box>
    <Box sx={{ width: 48, textAlign: "right", fontVariantNumeric: "tabular-nums", fontSize: 13 }}>{formatRate(rate)}</Box>
  </Box>
);

const DriversSection: React.FC<{ scope: Record<string, string | null | undefined> }> = ({ scope }) => {
  const theme = useTheme();
  const pair = PAIR[theme.palette.mode === "dark" ? "dark" : "light"];
  const { data, previousData, loading, error } = useQuery(REPORT_RETENTION_DRIVERS, {
    variables: { input: scope },
    fetchPolicy: "cache-and-network",
  });
  const drivers = (data ?? previousData)?.reportRetentionDrivers;
  const scale = drivers
    ? Math.max(0.01, ...drivers.signals.flatMap((row: any) => [row.withRate ?? 0, row.withoutRate ?? 0]), ...drivers.scores.map((row: any) => row.rate ?? 0))
    : 0;

  return (
    <Section
      title="What makes members stay"
      note={
        drivers ? (
          <>
            Of the {formatCount(drivers.members)} members who registered {formatDays({ from: drivers.from, to: drivers.to })},{" "}
            <b>{formatRate(drivers.baseRate)}</b> were still using the app 3 to 6 months later. Each row compares the members who did
            something in their first 14 days with those who did not. These are the eight early-adopter signals; worked out again on
            today's data.
          </>
        ) : (
          "Loading…"
        )
      }
    >
      <ReportError error={error} />
      {loading && !drivers && <LinearProgress />}
      {drivers && drivers.members === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nobody here has had their first 180 days yet, so there is nothing to measure.
        </Typography>
      )}
      {drivers && drivers.members > 0 && (
        <Stack spacing={3} sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 120ms" }}>
          <Box>
            <Box sx={{ display: "flex", gap: 2.5, mb: 1, fontSize: 13, color: "text.secondary", flexWrap: "wrap" }} aria-hidden>
              {[
                { label: "Did it in their first 14 days", color: pair.did },
                { label: "Did not", color: pair.didNot },
              ].map((item) => (
                <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: item.color }} />
                  {item.label}
                </Box>
              ))}
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>In their first 14 days, they…</TableCell>
                    <TableCell align="right">Members</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Still using the app 3 to 6 months later</TableCell>
                    <TableCell align="right">Compared with everyone</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drivers.signals.map((row: any) => (
                    <TableRow key={row.key}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCount(row.withMembers)}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <RateBar rate={row.withRate} scale={scale} color={pair.did} label={`Did it: ${formatRate(row.withRate)}`} />
                          <RateBar
                            rate={row.withoutRate}
                            scale={scale}
                            color={pair.didNot}
                            label={`Did not: ${formatRate(row.withoutRate)}`}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                        {row.lift === null ? "—" : `${formatLift(row.lift)} as likely`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              By early-adopter score
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              One point for each signal above. Three or more marks an early adopter: the first score that stays well above everyone
              else.
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Score</TableCell>
                    <TableCell align="right">Members</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Still using the app 3 to 6 months later</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drivers.scores.map((row: any) => (
                    <TableRow key={row.score}>
                      <TableCell sx={{ fontWeight: row.score >= 3 ? 600 : undefined }}>
                        {row.andAbove ? `${row.score} or more` : row.score}
                        {row.score === 3 && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            early adopter from here
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatCount(row.members)}
                      </TableCell>
                      <TableCell>
                        <RateBar
                          rate={row.rate}
                          scale={scale}
                          color={row.score >= 3 ? pair.did : pair.muted}
                          label={`Score ${row.score}: ${formatRate(row.rate)}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Stack>
      )}
    </Section>
  );
};

// ---- The tab ---------------------------------------------------------------

/**
 * The numbers that say whether members stay, against an earlier stretch of
 * days, with months-after-joining and what makes members stay underneath. It
 * compares two stretches of days, and shares only the region with the other reports.
 */
export const ScorecardTab: React.FC<{
  filters: ReportFilterState;
  preset: ComparePreset;
  custom: { current: DateRangeValue; previous: DateRangeValue };
}> = ({ filters, preset, custom }) => {
  const windows =
    preset === "custom"
      ? {
          current: { from: toDay(custom.current.from as Date), to: toDay(custom.current.to as Date) },
          previous: { from: toDay(custom.previous.from as Date), to: toDay(custom.previous.to as Date) },
        }
      : presetWindows(Number(preset));
  const scope = {
    timezone: browserTimezone(),
    regionCountry: filters.country?.name || null,
    regionState: filters.state?.name || null,
    regionCity: filters.city?.name || null,
  };

  const { data, previousData, loading, error } = useQuery(REPORT_SCORECARD, {
    variables: {
      input: {
        ...scope,
        from: windows.current.from,
        to: windows.current.to,
        compareFrom: windows.previous.from,
        compareTo: windows.previous.to,
      },
    },
    fetchPolicy: "cache-and-network",
  });
  const card = (data ?? previousData)?.reportScorecard;
  const metrics: Metric[] = card?.metrics ?? [];

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 320px" }}>
          {preset === "custom" ? "Comparing" : "Comparing the last"} {preset === "custom" ? "" : `${preset} complete days, `}
          <b>{formatDays(windows.current)}</b>, with <b>{formatDays(windows.previous)}</b>. Today is still in progress, so nothing
          counts it.
        </Typography>
        <ScorecardPdfButton card={card ?? null} scope={scope} region={regionLabel(filters)} />
      </Box>

      <ReportError error={error} />
      {loading && !card && <LinearProgress />}

      {card && (
        <Stack spacing={2} sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 120ms" }}>
          <Section
            title="At a glance"
            note="The five numbers that say most about whether members stay. Each shows this period, the change from the one compared with, and its last 12 weeks."
          >
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 1.5 }}>
              {metrics
                .filter((metric) => metric.headline)
                .map((metric) => (
                  <HeadlineTile key={metric.key} metric={metric} />
                ))}
            </Box>
          </Section>

          {SECTIONS.map((section) => {
            const rows = metrics.filter((metric) => metric.section === section.key);
            if (rows.length === 0) return null;
            return (
              <Section key={section.key} title={section.title} note={section.note || undefined}>
                <MetricTable metrics={rows} current={windows.current} previous={windows.previous} />
              </Section>
            );
          })}
        </Stack>
      )}

      <RetentionCurveSection scope={scope} />
      <DriversSection scope={scope} />
    </Stack>
  );
};
